<template>
  <div class="p-4">
    <!-- Кнопки "Добавить компанию" и "Запросить с 1С" -->
    <div class="flex space-x-2 mb-4">
      <button
        v-can-write="'companies'"
        @click="openAddCompanyModal"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        Добавить компанию
      </button>
      <button
        v-can-write="'companies'"
        @click="fetchCompaniesFromOneC"
        class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
      >
        Запросить из 1С
      </button>
    </div>

    <!-- Состояние загрузки -->
    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Таблица компаний -->
    <template v-else>
      <table v-if="companies.length > 0" class="w-full border-collapse bg-white shadow-md">
      <thead class="bg-gray-100">
        <tr>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">ID</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Название компании</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">ИНН</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Адрес</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Действия</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="company in companies" :key="company.id" class="border-b hover:bg-gray-50">
          <td class="px-4 py-2 text-sm text-gray-700">{{ company.id }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ company.name }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ company.inn }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ company.address || '-' }}</td>
          <td class="px-4 py-2">
            <div class="flex space-x-2">
              <button
                v-can-write="'companies'"
                @click="editCompany(company)"
                class="px-2 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Редактировать
              </button>
              <button
                v-can-write="'companies'"
                @click="confirmDelete(company.id)"
                class="px-2 py-1 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Удалить
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <Pagination
      v-if="pagination.lastPage > 1"
      :current-page="pagination.currentPage"
      :last-page="pagination.lastPage"
      :total="pagination.total"
      :per-page="pagination.perPage"
      @change-page="changePage"
    />
    </template>

    <!-- Модальное окно для добавления компании -->
    <AddCompanyModal ref="addCompanyModal" :company="selectedCompany" @company-saved="getCompanies"></AddCompanyModal>

    <!-- Модальное окно подтверждения удаления -->
    <div v-if="isConfirmModalVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">Подтвердите удаление</h2>
        <p class="text-sm text-gray-700 mb-4">Вы уверены, что хотите удалить эту компанию?</p>
        <div class="flex justify-end space-x-2">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Отмена
          </button>
          <button
            @click="proceedDelete"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AddCompanyModal from '@/components/AddCompanyModal.vue';
import Pagination from '@/components/Pagination.vue';
import { createUrlStateManager } from '@/utils/urlStateManager';

export default {
  components: { AddCompanyModal, Pagination },
  data() {
    return {
      companies: [],
      loading: true,
      isConfirmModalVisible: false,
      companyIdToDelete: null,
      selectedCompany: null,
      page: 1,
      perPage: 20,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        perPage: 20,
      },
    };
  },
  created() {
    createUrlStateManager(this, {
      page: { type: 'number', default: 1, urlKey: 'page' },
    }, { routeName: 'Companies' });

    this.getCompanies();
  },
  methods: {
    getCompanies() {
      this.loading = true;
      axios
        .post('/api/company/getCompanies', {
          page: this.page,
          per_page: this.perPage,
        })
        .then((response) => {
          const d = response.data.data;
          this.companies = d.data || [];
          this.pagination = {
            currentPage: d.current_page,
            lastPage: d.last_page,
            total: d.total,
            perPage: d.per_page,
          };
          this.loading = false;
        })
        .catch((error) => {
          console.error(error);
          this.loading = false;
        });
    },
    changePage(page) {
      this.page = page;
      this.getCompanies();
    },

    editCompany(company) {
      this.selectedCompany = company;
      if (!this.$refs.addCompanyModal) {
        return;
      }
      this.$refs.addCompanyModal.show();
    },

    confirmDelete(id) {
      this.companyIdToDelete = id; // Сохраняем ID компании
      this.isConfirmModalVisible = true; // Показываем модальное окно
    },

    cancelDelete() {
      this.isConfirmModalVisible = false; // Скрываем модальное окно
      this.companyIdToDelete = null; // Очищаем ID
    },

    proceedDelete() {
      if (!this.companyIdToDelete) {
        console.error('ID компании не указан');
        return;
      }

      axios
        .post('/api/company/deleteCompany', { id: this.companyIdToDelete })
        .then(() => {
          this.getCompanies(); // Обновляем список компаний
          this.cancelDelete(); // Скрываем модальное окно
        })
        .catch((error) => {
          console.error(error);
        });
    },

    openAddCompanyModal() {
      this.selectedCompany = null;
      if (!this.$refs.addCompanyModal) {
        return;
      }
      this.$refs.addCompanyModal.show();
    },

    fetchCompaniesFromOneC() {
      this.loading = true;

      axios
        .post('/api/oneC/getCompanies')
        .then((response) => {
          console.log('Компании из 1С:', response.data.data);
        })
        .catch((error) => {
          console.error('Ошибка при запросе компаний из 1С:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
